<template>
    <transition name="fade">
        <article
            v-show="illustrationLoaded"
            class="print-box print-box--generation-information"
            :class="{
                'generated': isGenerated,
            }"
        >
            <section class="generation">
                <section class="generation__illustration">
                    <img
                        :src="this[generationStatus].illustration"
                        alt="Person holding card"
                        @load="onIllustrationLoad"
                    >
                    <div
                        v-if="isGenerating && illustrationLoaded"
                        class="generation__illustration__orders-details"
                    >
                        <progress-circle
                            :percentage="processedOrdersPercentage"
                        ></progress-circle>
                        <p class="generation__counter">
                            {{`${ processedOrdersCounter } / ${allOrders.length}`}}
                        </p>
                    </div>
                </section>
                <h2 class="generation__title">{{this[generationStatus].title}}</h2>
                <p
                    class="generation__subtitle"
                    :class="{
                        'noSeparator': !isGenerating,
                    }"
                    v-html="this[generationStatus].subtitle"
                ></p>
                <section
                    v-show="isGenerating"
                    class="generation__explanations"
                >
                    <div class="generation__explanations__text1">
                        <svg class="ico"><use xlink:href="/images/svg-symbols.svg#box"></use></svg>
                        <p>
                            Use a página Meus Envios para gerenciar suas etiquetas
                        </p>
                    </div>
                    <p class="generation__explanations__text2">
                        As etiquetas já geradas e as etiquetas em processo de geração também ficam disponíveis na página Meus Envios.
                        Você pode diferenciá-las pelos ícones que ficam no canto direito.
                    </p>
                    <div class="generation__explanations__icons">
                        <div class="icons-wrapper">
                            <p class="icons-tooltip">Etiqueta ainda em <br> geração</p>
                            <div class="icons-card icons-card--loading">
                                <loader-spinner-new class="ico"></loader-spinner-new>
                            </div>
                        </div>
                        <div class="icons-wrapper">
                            <div class="icons-card icons-card--loaded">
                                <svg class="ico ico--printer"><use xlink:href="/images/svg-symbols.svg#imprimirok"></use></svg>
                                <svg class="ico ico--trash"><use xlink:href="/images/svg-symbols.svg#trash"></use></svg>
                            </div>
                            <p class="icons-tooltip">Etiqueta pronta para <br> impressão</p>
                        </div>
                    </div>
                    <p class="generation__explanations__text3">
                        <span>Atenção:</span> só é possível cancelar etiquetas que já estejam prontas para impressão.
                    </p>
                </section>
                <section
                    v-show="isCompanyError || isInternalError"
                    class="generation__explanations"
                >
                    <div class="alert alert--orderGeneration">
                        <svg class="ico"><use xlink:href="/images/svg-symbols.svg#error"></use></svg>
                        <div class="alert__texts">
                            <p class="alert__title">Por favor, tente gerar suas etiquetas novamente.</p>
                            <p class="alert__subtitle">Falha ao processar as seguintes etiquetas:</p>
                            <p
                                class="alert__errorText"
                                v-for="failedOrder in failedOrders"
                                :key="failedOrder.id"
                            >
                                <b>
                                {{ failedOrder.protocol }}
                                ({{ failedOrder.service.company.name }}):
                                </b>
                                {{ failedOrder.generated_key.data }}
                            </p>
                        </div>
                    </div>

                    <p
                        v-show="generationStatus === 'internalError'"
                        class="generation__explanations__csTip"
                    >
                        <span>Dica:</span> informe o erro para que possamos otimizar o seu atendimento.
                    </p>
                </section>
                <a
                    v-show="isGenerating"
                    class="btnBorder btnRedirectMeusEnvios" href="/painel/meus-envios"
                >
                    Ir para meus envios
                </a>
                <button
                    class="btnBorder btnSeeTags"
                    @click="$parent.handlePrint"
                >
                    Visualizar Etiquetas
                </button>
                <div
                    v-show="isCompanyError || isInternalError"
                    class="generation__buttons"
                >
                    <a
                        v-show="isCompanyError"
                        href="javascript;"
                        class="btnConfirm"
                        @click.prevent="generate"
                    >
                        Tentar novamente
                    </a>
                    <a
                        v-show="isInternalError"
                        :href="supportDashUrl"
                        class="btnConfirm"
                    >
                        Falar com o suporte
                    </a>
                    <a href="javascript;" class="btnColor">
                        <span>Imprimir etiquetas</span>
                        <svg class="ico"><use xlink:href="/images/svg-symbols.svg#print"></use></svg>
                    </a>
                </div>
                <p
                    v-show="isCompanyError"
                    class="generation__companyErrorTip"
                >
                    Caso o erro continue, por favor, aguarde alguns momentos e tente novamente pela página <a href="/painel/meus-envios">Meus Envios</a>.
                </p>
            </section>
        </article>
    </transition>
</template>

<script>
import LoaderSpinnerMelhorUI from 'commons/js/components/atoms/LoaderSpinnerMelhorUI.vue';
import ProgressCircle from 'commons/js/components/atoms/ProgressCircle.vue';
import { getPrintPreference } from 'commons/js/utils/print';

export default {
    props: {
        allOrders: {
            required: false,
			default: function() {
                return [];
            },
        },
        generatedOrders: {
            required: false,
        },
    },

    components: {
        'loader-spinner-new': LoaderSpinnerMelhorUI,
        'progress-circle': ProgressCircle,
    },

    computed: {
        failedOrders() {
            return this.allOrders.filter(order => order.generated_key && order.generated_key.failed_at);
        },

        allOrdersProcessed() {
            return this.generatedOrders.length + this.failedOrders.length === this.allOrders.length;
        },

        processedWithError() {
            return !!this.failedOrders.length;
        },

        generationStatus() {
            if (this.allOrdersProcessed) {
                if (this.processedWithError) {
                    const triedThreeTimes = this.failedOrders.some(order => order.generated_key.attempts === 3);
                    return triedThreeTimes ? 'companyError' : 'internalError';
                }
                return 'generated';
            }
            return 'generating';
        },

        isGenerating() {
            return this.generationStatus === 'generating';
        },

        isGenerated() {
            return this.generationStatus === 'generated';
        },

        isCompanyError() {
            return this.generationStatus === 'companyError';
        },

        isInternalError() {
            return this.generationStatus === 'internalError';
        },

        processedOrdersCounter() {
          return this.generatedOrders.length + this.failedOrders.length;
        },

        processedOrdersPercentage() {
            return (this.processedOrdersCounter * 100) / this.allOrders.length;
        },
    },

    watch: {
        generationStatus: {
            immediate: true,
            handler(value) {
                if (value === 'generating') {
                    this.$emit('generating');
                }

                if (['generated', 'companyError', 'internalError'].includes(value)) {
                    this.$emit('generated');
                }

                if (value === 'generated') {
                    this.$emit('generated');
                }
            },
        },
    },

    data()  {
        return {
            status: [
                'generating',
                'generated',
                'companyError',
                'internalError',
            ],
            generating: {
                illustration: '/images/svg/person-illustration-1.svg',
                title: 'Estamos gerando suas etiquetas!',
                subtitle: 'Você pode aguardar nesta página ou continuar navegando enquanto processamos seus envios.',
            },
            generated: {
                illustration: '/images/svg/person-illustration-2.svg',
                title: 'Suas etiquetas foram geradas com sucesso!',
                subtitle: 'Confira como ficaram suas etiquetas.',
            },
            companyError: {
                illustration: '/images/svg/person-illustration-3.svg',
                title: 'Nem todas as etiquetas foram geradas',
                subtitle: `Um erro no sistema da transportadora impediu a geração de uma ou mais etiquetas.
                    Você pode imprimir as etiquetas que já foram geradas e tentar gerar novamente as que apresentaram erro.
                    <a href="https://menv.io/errogeracao">Saiba mais</a>.`
            },
            internalError: {
                illustration: '/images/svg/person-illustration-3.svg',
                title: 'Nem todas as etiquetas foram geradas',
                subtitle: `Um erro de sistema impediu a geração de uma ou mais etiquetas, mas você já pode imprimir as etiquetas que não apresentaram erro.
                    Para receber ajuda com as etiquetas que não foram geradas, fale com o nosso suporte.
                    <br><a href="https://menv.io/errogeracao">Saiba mais</a>.`
            },
            supportDashUrl: MEConfig.get('dash.url') + '/ajuda',
            illustrationLoaded: false,
        }
    },

    methods: {
        toggleTagDialog() {
            this.$emit('toggleTagDialog');
        },

        async generate() {
            try {
                const orders = this.failedOrders.map(order => order.id);

                const printQuality = getPrintPreference();

                const {data} = await axios.post('/api/v2/me/shipment/tag/generate', {orders, printQuality});

                const bc = new BroadcastChannel('background-tags');
                bc.postMessage('start-generation');

                delete data.generate_key;

                const ordersIds = Object.keys(data);

                const {data: {url}} = await axios.post('/api/v2/me/shipment/print', {orders: ordersIds});

                const redirectUrl = url || '/painel/meus-envios';

                window.open(redirectUrl);
            } catch (error) {
                console.error(error);
            }
        },

        onIllustrationLoad() {
            this.$emit('on-illustration-load');
            this.illustrationLoaded = true;
        },
    },
}
</script>
